import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Box, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import {
  WarningAmberRounded as WarningIcon,
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon,
  MonitorHeart as VitalsIcon,
  EditNote as NoteIcon,
} from '@mui/icons-material';
import { admissionCompliance } from '../BaseURL';

const WARN_BG     = 'rgba(234,179,8,0.10)';
const WARN_BORDER = 'rgba(234,179,8,0.35)';
const WARN        = '#b45309';
const OK_BG       = 'rgba(65,163,134,0.08)';
const OK_BORDER   = 'rgba(65,163,134,0.30)';
const OK          = '#2f8068';
const DANGER      = '#b91c1c';
const DANGER_BG   = 'rgba(220,38,38,0.10)';

/**
 * Slim compliance banner for the currently-selected admitted patient.
 * Shows today's vitals & notes progress against the per-day minimum.
 *
 * Props:
 *   patientId      patient folder id (required)
 *   admissionDate  YYYY-MM-DD of admission (required)
 *   refreshKey     bump this to force a refresh after a vitals/note save
 */
const ComplianceBanner = ({ patientId, admissionDate, refreshKey = 0 }) => {
  const [loading, setLoading] = useState(false);
  const [today, setToday]     = useState(null);
  const [exempt, setExempt]   = useState(false);

  const fetchData = useCallback(async () => {
    if (!patientId || !admissionDate) return;
    setLoading(true);
    try {
      // Hard 12s timeout so a slow / hung request doesn't pin the banner
      // in a permanent loading state. On timeout we just hide the banner.
      const { data } = await axios.get(admissionCompliance, {
        params: { patientId, admissionDate },
        timeout: 12000,
      });
      if (data?.success) {
        setExempt(!!data.exempt);
        if (data.exempt) {
          setToday(null);
        } else {
          const ymd = new Date();
          const y = ymd.getFullYear();
          const m = String(ymd.getMonth() + 1).padStart(2, '0');
          const d = String(ymd.getDate()).padStart(2, '0');
          const todayStr = `${y}-${m}-${d}`;
          const match = (data.days || []).find(x => x.date === todayStr) || null;
          setToday(match);
        }
      } else {
        // Backend explicitly returned success=false → treat as no data.
        setToday(null);
      }
    } catch (err) {
      console.error('Compliance banner load failed:', err.message || err);
      // Network/timeout/server error → quietly hide the banner rather
      // than spinning forever.
      setToday(null);
      setExempt(true);
    } finally {
      setLoading(false);
    }
  }, [patientId, admissionDate]);

  useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

  if (!patientId || !admissionDate || exempt) return null;

  // No spinner: the banner just appears once data is ready. Anything that
  // would keep it loading (slow DB, missing indexes) now gracefully fails
  // to a hidden banner after the 12s timeout — never a stuck UI.
  if (!today) return null;

  const isComplete = today.complete;
  const bg     = isComplete ? OK_BG     : WARN_BG;
  const border = isComplete ? OK_BORDER : WARN_BORDER;
  const fg     = isComplete ? OK        : WARN;
  // Backend now ships per-row minimums on each day record (vitals=3, notes=1).
  // Fall back to the documented defaults if a transitional response misses them.
  const minVitals = today.minVitals != null ? today.minVitals : 3;
  const minNotes  = today.minNotes  != null ? today.minNotes  : 1;

  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        px: 1.25, py: 0.75, mb: 1,
        borderRadius: 1,
        bgcolor: bg,
        border: `1px solid ${border}`,
      }}
    >
      {isComplete
        ? <CheckIcon sx={{ fontSize: 18, color: fg }} />
        : <WarningIcon sx={{ fontSize: 18, color: fg }} />}
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: fg, letterSpacing: '0.3px' }}>
        TODAY'S COMPLIANCE
      </Typography>
      <Chip
        size="small"
        icon={<VitalsIcon sx={{ fontSize: 12 }} />}
        label={`Vitals ${today.vitals}/${minVitals}`}
        sx={{
          height: 20, fontSize: '0.7rem', fontWeight: 600,
          bgcolor: today.missingVitals === 0 ? OK_BG : DANGER_BG,
          color:   today.missingVitals === 0 ? OK    : DANGER,
          border: `1px solid ${today.missingVitals === 0 ? OK_BORDER : 'rgba(220,38,38,0.3)'}`,
          '& .MuiChip-icon': { color: 'inherit', ml: '4px' },
        }}
      />
      <Chip
        size="small"
        icon={<NoteIcon sx={{ fontSize: 12 }} />}
        label={`Notes ${today.notes}/${minNotes}`}
        sx={{
          height: 20, fontSize: '0.7rem', fontWeight: 600,
          bgcolor: today.missingNotes === 0 ? OK_BG : DANGER_BG,
          color:   today.missingNotes === 0 ? OK    : DANGER,
          border: `1px solid ${today.missingNotes === 0 ? OK_BORDER : 'rgba(220,38,38,0.3)'}`,
          '& .MuiChip-icon': { color: 'inherit', ml: '4px' },
        }}
      />
      {!isComplete && (
        <Typography sx={{ fontSize: '0.74rem', color: fg, fontStyle: 'italic' }}>
          Minimum {minVitals} vital{minVitals === 1 ? '' : 's'} & {minNotes} note{minNotes === 1 ? '' : 's'} per day
        </Typography>
      )}
      <Box sx={{ flex: 1 }} />
      <Tooltip title="Refresh">
        <IconButton size="small" onClick={fetchData} sx={{ color: fg }}>
          <RefreshIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default ComplianceBanner;
